import React, { useState, useRef, useEffect } from "react";
import { Search as SearchIcon, MoreVertical } from "lucide-react";

export function Button({ variant = "secondary", size, loading, className = "", children, tap, ...props }) {
  const cls = [
    "btn",
    variant === "primary" && "btn-primary",
    variant === "ghost" && "btn-ghost",
    variant === "danger" && "btn-danger",
    size === "sm" && "btn-sm",
    tap && "tap",
    loading && "is-loading",
    className,
  ].filter(Boolean).join(" ");
  return (
    <button className={cls} disabled={loading || props.disabled} {...props}>
      {loading ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, htmlFor, children }) {
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      {label ? <label className="t-label lbl" htmlFor={htmlFor}>{label}</label> : null}
      {children}
      {error ? <span className="err-text">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Input(props) { return <input className="input" {...props} />; }
export function Textarea(props) { return <textarea className="textarea" {...props} />; }
export function Select({ children, ...props }) { return <select className="select" {...props}>{children}</select>; }

export function Search({ ...props }) {
  return (
    <div className="search">
      <SearchIcon className="icon" size={20} strokeWidth={1.75} aria-hidden="true" />
      <input className="input" type="search" {...props} />
    </div>
  );
}

export function Card({ interactive, selected, footer, className = "", children, ...props }) {
  const cls = ["card", interactive && "interactive", selected && "selected", className].filter(Boolean).join(" ");
  return (
    <div className={cls} {...props}>
      {children}
      {footer ? <div className="card-footer">{footer}</div> : null}
    </div>
  );
}

export function Chip({ tone = "neutral", dot, children, ...rest }) {
  const cls = ["chip", tone !== "neutral" && tone].filter(Boolean).join(" ");
  return <span className={cls} {...rest}>{dot ? <span className="dot" /> : null}{children}</span>;
}

export function EmptyState({ icon, title, action }) {
  const renderedIcon = icon && !React.isValidElement(icon)
    ? React.createElement(icon, { size: 20, strokeWidth: 1.75 })
    : icon;
  return (
    <div className="empty">
      {icon ? <div className="empty-icon">{renderedIcon}</div> : null}
      <p className="t-body">{title}</p>
      {action || null}
    </div>
  );
}

export function Skeleton({ variant = "line", style }) {
  const cls = variant === "title" ? "sk sk-title" : variant === "card" ? "sk sk-card" : "sk sk-line";
  return <div className={cls} style={style} aria-hidden="true" />;
}

export function Avatar({ src, name }) {
  if (src) return <img className="avatar" src={src} alt={name || ""} />;
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <span className="initials" aria-hidden="true">{initials}</span>;
}

export function ThreeDotMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="menu" ref={ref}>
      <button className="menu-btn" aria-label="More" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <MoreVertical size={20} strokeWidth={1.75} />
      </button>
      {open ? (
        <div className="menu-pop" role="menu">
          {items.map((it, i) => (
            <button key={i} role="menuitem" onClick={() => { setOpen(false); it.onClick?.(); }}>{it.label}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" aria-selected={value === t.value}
          className={`tab${value === t.value ? " active" : ""}`} onClick={() => onChange(t.value)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Breadcrumb({ items }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <span aria-hidden="true">/</span> : null}
          {it.href ? <a href={it.href}>{it.label}</a> : <span>{it.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function Pagination({ page, pages, onPage }) {
  return (
    <div className="pager">
      <button className="pg" disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <button key={p} className={`pg${p === page ? " active" : ""}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="pg" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

export function Modal({ open, title, children, onClose, footer }) {
  if (!open) return null;
  return (
    <div className="overlay center" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="modal">
        {title ? <h2 className="t-h2" style={{ marginBottom: 12 }}>{title}</h2> : null}
        {children}
        {footer ? <div className="actions">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Drawer({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="overlay right" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="drawer">
        {title ? <h2 className="t-h2" style={{ marginBottom: 12 }}>{title}</h2> : null}
        {children}
      </div>
    </div>
  );
}

export function Toast({ tone = "neutral", children, ...rest }) {
  return <div className={`toast${tone !== "neutral" ? " " + tone : ""}`} role="status" {...rest}>{children}</div>;
}

export function Tooltip({ label, children }) {
  return <span className="tip" tabIndex={0}>{children}<span className="tip-body" role="tooltip">{label}</span></span>;
}
